const DARK_TEXT = "#1A1A1A";
const LIGHT_TEXT = "#FFFFFF";

export default class ColorUtil {

    /**
     * Parses #RGB, #RGBA, #RRGGBB and #RRGGBBAA into { r, g, b, a } (a is 0-1).
     * Returns undefined when the value cannot be parsed.
     */
    static parse(color) {
        if (typeof color !== "string") {
            return undefined;
        }

        let hex = color.trim().replace(/^#/, '');
        if (hex.length === 3 || hex.length === 4) {
            hex = hex.split('').map(channel => channel + channel).join('');
        }
        if ((hex.length !== 6 && hex.length !== 8) || !/^[0-9a-fA-F]+$/.test(hex)) {
            return undefined;
        }

        return {
            r: parseInt(hex.substring(0, 2), 16),
            g: parseInt(hex.substring(2, 4), 16),
            b: parseInt(hex.substring(4, 6), 16),
            a: hex.length === 8 ? parseInt(hex.substring(6, 8), 16) / 255 : 1,
        };
    }

    static toHexChannel(value) {
        const clamped = Math.min(255, Math.max(0, Math.round(value)));
        return ('0' + clamped.toString(16)).slice(-2);
    }

    static adjust(color, amount) {
        const rgba = ColorUtil.parse(color);
        if (rgba === undefined) {
            return color;
        }

        // Shift the color channels only, the alpha channel is preserved as-is.
        const alpha = rgba.a < 1 ? ColorUtil.toHexChannel(rgba.a * 255) : '';
        return `#${ColorUtil.toHexChannel(rgba.r + amount)}${ColorUtil.toHexChannel(rgba.g + amount)}${ColorUtil.toHexChannel(rgba.b + amount)}${alpha}`;
    }

    /**
     * Returns the relative luminance (0-1) of a color, composited over white so
     * translucent colors are measured the way they actually render on the map.
     */
    static luminance(color) {
        const rgba = ColorUtil.parse(color);
        if (rgba === undefined) {
            return 1;
        }

        const channel = (value) => {
            const blended = (value / 255) * rgba.a + (1 - rgba.a);
            return blended <= 0.03928 ? blended / 12.92 : Math.pow((blended + 0.055) / 1.055, 2.4);
        };

        return 0.2126 * channel(rgba.r) + 0.7152 * channel(rgba.g) + 0.0722 * channel(rgba.b);
    }

    /**
     * Darkens a color if it is too light to read against a white background,
     * keeping marker labels legible regardless of the configured response color.
     */
    static ensureReadable(hex) {
        if (this.luminance(hex) > 0.7) {
            return this.adjust(hex, -90);
        }
        return hex;
    }

    /**
     * Picks the text color (near black or white) that reads best on top of the given
     * background. Used for labels drawn directly over a filled parcel.
     */
    static contrastText(background) {
        return ColorUtil.luminance(background) > 0.4 ? DARK_TEXT : LIGHT_TEXT;
    }

    /**
     * Outline color to pair with a text color so labels stay legible even when the
     * surface behind them is not exactly the color they were measured against.
     */
    static haloFor(textColor) {
        return textColor === LIGHT_TEXT ? "rgba(0,0,0,0.85)" : "rgba(255,255,255,0.9)";
    }
}